import { useState } from "react";
import { LangLink } from "@/components/LangLink";
import { Seo } from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import {
  Shirt, Layers, Wind, Shield, Link2, Sparkles, Dog, Package, User,
  ArrowRight, Droplets, Sun, Snowflake, AlertTriangle,
} from "lucide-react";

// Care guidance organised by the same categories the store is browsed by, so a
// rider can jump straight to the product type they need help with. Only one
// category is shown at a time (no infinite scroll).
const CATEGORIES = [
  {
    slug: "horse-rugs", name: "Horse Rugs & Coolers", icon: Layers,
    intro: "Turnout rugs, stable rugs, coolers and exercise sheets — washed and re-proofed correctly, they last for many seasons.",
    tips: [
      { h: "Washing", p: "Brush off loose dirt and hair first. Machine wash most Kentucky rugs at 30°C on a gentle cycle using a specialist technical wash (never standard detergent or fabric softener, which clog the fabric and strip waterproofing)." },
      { h: "Re-proofing waterproof rugs", p: "Waterproofing wears down with use and washing. Re-proof turnout rugs with a wash-in or spray-on technical proofer once water stops beading on the surface." },
      { h: "Drying", p: "Air dry flat or over a rail away from direct heat. Never tumble dry — heat damages waterproof membranes, fillings and bindings." },
      { h: "Repairs & storage", p: "Fix small tears promptly so they don't spread. Store fully clean and bone-dry in a breathable bag to prevent mould and odours." },
    ],
    quick: [{ icon: Droplets, t: "Wash at 30°C, technical detergent" }, { icon: Snowflake, t: "Never tumble dry" }, { icon: Shield, t: "Re-proof when water stops beading" }],
  },
  {
    slug: "saddle-pads", name: "Saddle Pads & Numnahs", icon: Shirt,
    intro: "Keep saddle pads fresh and shapely so they stay comfortable against your horse's back.",
    tips: [
      { h: "Before washing", p: "Remove hair and sweat with a rubber curry or soft brush while dry — this prevents a felted, matted finish after washing." },
      { h: "Machine wash", p: "Wash at 30°C with a mild, non-bio detergent. Fasten any velcro tabs before washing so they don't snag the fabric or pick up hair." },
      { h: "Drying & shape", p: "Air dry naturally and reshape while damp. Avoid radiators and tumble dryers, which shrink and harden the padding." },
    ],
    quick: [{ icon: Droplets, t: "30°C, mild detergent" }, { icon: Sun, t: "Air dry, reshape damp" }],
  },
  {
    slug: "fly-veils", name: "Fly Veils & Ear Bonnets", icon: Wind,
    intro: "Delicate crochet and soft ear nets need gentle handling to keep their shape and stretch.",
    tips: [
      { h: "Gentle wash", p: "Hand wash or use a delicates bag on a 30°C cool cycle with mild detergent to protect crochet work and elastic." },
      { h: "Drying", p: "Reshape and dry flat. Avoid wringing, hanging by the ears, or any direct heat that can distort the fit." },
    ],
    quick: [{ icon: Droplets, t: "Hand wash / delicates bag" }, { icon: Sun, t: "Dry flat, reshape" }],
  },
  {
    slug: "boots-bandages", name: "Boots & Bandages", icon: Shield,
    intro: "Tendon, fetlock, brushing and travel boots — plus bandages — perform best when kept clean and dry.",
    tips: [
      { h: "After every use", p: "Rinse off mud and sweat and let boots dry naturally before the next wear. Trapped grit rubs and can cause skin irritation." },
      { h: "Neoprene & synthetic liners", p: "Wipe or rinse with cool water and a little mild soap. Don't soak for long periods and keep away from direct heat." },
      { h: "Sheepskin / faux-fur linings", p: "Spot clean and brush gently once dry to lift the pile. Avoid hot washes that flatten and harden the lining." },
      { h: "Bandages", p: "Machine wash at 30°C in a wash bag, roll up neatly once fully dry, and store flat so the elastic keeps its stretch." },
    ],
    quick: [{ icon: Droplets, t: "Rinse after each use" }, { icon: Snowflake, t: "Dry away from heat" }],
  },
  {
    slug: "bridles-tack", name: "Bridles, Reins & Leatherwork", icon: Link2,
    intro: "Dy'on and Kentucky leatherwork rewards regular care with a supple feel, deep colour and a long working life.",
    tips: [
      { h: "Clean", p: "Wipe down after riding with a damp cloth to remove sweat and grease. Use a quality glycerine saddle soap for a deeper clean — work into a light lather, don't saturate." },
      { h: "Condition", p: "Apply leather balm or conditioner regularly (more often in dry or cold weather) to prevent the leather drying out and cracking. Pay attention to fold points and keeper loops." },
      { h: "Metalwork", p: "Polish buckles and bits with a soft dry cloth. Rinse bits in clean water after use — avoid harsh chemicals near the mouthpiece." },
      { h: "Storage", p: "Hang bridles on a rounded rack and store in a cool, dry, well-ventilated place. Never dry wet leather by a heater, which causes cracking." },
    ],
    quick: [{ icon: Sparkles, t: "Saddle soap, then condition" }, { icon: AlertTriangle, t: "Never dry by direct heat" }],
  },
  {
    slug: "grooming", name: "Grooming Tools & Coat Care", icon: Sparkles,
    intro: "Grooming Deluxe brushes and coat-care products stay hygienic and effective with simple routine care.",
    tips: [
      { h: "Brushes", p: "Clean brushes regularly with a curry comb to remove hair and dust, then wash in warm soapy water as needed and dry bristle-side down so water doesn't sit in the wooden back." },
      { h: "Sprays, gels & shampoos", p: "Close lids tightly and store at room temperature, away from frost and direct sunlight. Shake well before use." },
      { h: "Grooming bags & boxes", p: "Empty and wipe out regularly. Let damp tools dry fully before storing to avoid mildew and odours." },
    ],
    quick: [{ icon: Droplets, t: "Wash brushes, dry bristles-down" }, { icon: Snowflake, t: "Keep liquids frost-free" }],
  },
  {
    slug: "dog", name: "Dog Coats, Beds & Accessories", icon: Dog,
    intro: "Kentucky dog coats, beds, collars and leads are built to the same standard as the horse range.",
    tips: [
      { h: "Dog coats", p: "Machine wash at 30°C with a technical wash, avoid fabric softener, and air dry. Re-proof waterproof coats when they stop repelling water." },
      { h: "Beds", p: "Remove and wash covers at 30°C. Air dry fully before reuse; never tumble dry padded inners." },
      { h: "Collars, leads & harnesses", p: "Wipe down with a damp cloth and mild soap. Check stitching and fastenings regularly for wear." },
    ],
    quick: [{ icon: Droplets, t: "Covers wash at 30°C" }, { icon: Sun, t: "Air dry only" }],
  },
  {
    slug: "stable-accessories", name: "Stable & Bags", icon: Package,
    intro: "Stable curtains, tack-room bags, racks and buckets — a quick routine keeps your yard tidy and kit in good order.",
    tips: [
      { h: "Bags & curtains", p: "Spot clean fabric with a damp cloth and mild soap; many bags can be gently machine washed at 30°C in a wash bag. Air dry fully before storing." },
      { h: "Racks, hooks & buckets", p: "Wipe metal and plastic with warm soapy water and dry to prevent rust marks and limescale build-up." },
    ],
    quick: [{ icon: Droplets, t: "Spot clean, mild soap" }, { icon: Sun, t: "Dry before storing" }],
  },
  {
    slug: "rider", name: "Rider Apparel & Accessories", icon: User,
    intro: "Competition shirts, jackets, breeches, socks, gloves and base layers stay crisp with the right wash.",
    tips: [
      { h: "Technical clothing", p: "Wash at 30°C on a gentle cycle, inside out, with a non-bio detergent. Skip fabric softener — it coats technical fibres and reduces breathability and wicking." },
      { h: "Drying", p: "Air dry away from direct heat to protect elastane, prints and competition trims. Reshape collars and cuffs while damp." },
      { h: "Gloves & accessories", p: "Hand wash gloves in cool water, press out (don't wring) and dry flat. Store belts and ties rolled or hung to keep their shape." },
    ],
    quick: [{ icon: Droplets, t: "30°C inside out, non-bio" }, { icon: Snowflake, t: "No softener, no tumble" }],
  },
];

export default function CareGuide() {
  const { tx } = useI18n();
  const [active, setActive] = useState(0);
  const cat = CATEGORIES[active];
  const Icon = cat.icon;

  return (
    <>
      <Seo title="Care Guide — Kentucky, Dy'on, Velari & Grooming Deluxe"
        description="How to wash, condition and store every type of equestrian equipment — rugs, saddle pads, boots, bridles, grooming tools, rider apparel and more."
        path="/care-guide" />

      <div className="border-b border-border bg-[hsl(var(--surface-2))]">
        <div className="container-site py-12 lg:py-16">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))]">{tx("Care Guide")}</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">{tx("Care for every category")}</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            {tx("Choose a category to see exactly how to wash, condition and store your equipment so it performs season after season.")}
          </p>
        </div>
      </div>

      {/* Mobile category selector (horizontal chips) */}
      <div className="border-b border-border lg:hidden">
        <div className="container-site flex gap-2 overflow-x-auto py-3" data-testid="care-mobile-nav">
          {CATEGORIES.map((c, i) => (
            <button key={c.slug} onClick={() => setActive(i)} data-testid={`care-chip-${c.slug}`}
              className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                i === active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"
                             : "border-border text-muted-foreground hover:border-foreground/30"}`}>
              {tx(c.name)}
            </button>
          ))}
        </div>
      </div>

      <div className="container-site grid gap-10 py-10 lg:grid-cols-[260px_1fr] lg:py-14">
        {/* Desktop sticky category rail */}
        <aside className="hidden lg:block">
          <nav className="sticky top-28 space-y-1" data-testid="care-side-nav">
            {CATEGORIES.map((c, i) => {
              const CIcon = c.icon;
              return (
                <button key={c.slug} onClick={() => setActive(i)} data-testid={`care-nav-${c.slug}`}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                    i === active ? "bg-[hsl(var(--primary))]/10 font-medium text-[hsl(var(--primary))]"
                                 : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                  <CIcon className="h-4 w-4 shrink-0" />
                  <span>{tx(c.name)}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Active category panel */}
        <section key={cat.slug} data-testid="care-panel">
          <div className="flex items-start gap-4">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
              <Icon className="h-6 w-6" />
            </span>
            <div>
              <h2 className="font-display text-2xl sm:text-3xl">{tx(cat.name)}</h2>
              <p className="mt-2 max-w-2xl text-muted-foreground">{tx(cat.intro)}</p>
            </div>
          </div>

          {/* Quick facts */}
          <div className="mt-6 flex flex-wrap gap-2">
            {cat.quick.map((q, i) => {
              const QIcon = q.icon;
              return (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-foreground/80">
                  <QIcon className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />{tx(q.t)}
                </span>
              );
            })}
          </div>

          {/* Detailed tips */}
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {cat.tips.map((tp, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-display text-lg">{tx(tp.h)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tx(tp.p)}</p>
              </div>
            ))}
          </div>

          {/* Shop CTA */}
          <div className="mt-8 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-secondary/40 p-5">
            <p className="mr-auto text-sm text-muted-foreground">{tx("Looking for new")} <strong className="text-foreground">{tx(cat.name)}</strong>?</p>
            <Button asChild data-testid="care-shop-button">
              <LangLink to={`/collection/${cat.slug}`}>{tx("Shop")} {tx(cat.name)} <ArrowRight className="ml-2 h-4 w-4" /></LangLink>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
}
